import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { store } from '@/state/store';
import { logout, tokenRefreshed } from '@/state/authSlice';
import type { ApiResponse } from '@/types/api.types';
import type { LoginResponse } from '@/types/auth.types';

type RequestConfigWithRetry = InternalAxiosRequestConfig & { _retry?: boolean };

// Use the Vite dev proxy (/api → :5000) by default; override via VITE_API_BASE_URL for prod.
// (Previously hard-coded to :5055, which is a stale/dead port — see integration Phase 0 findings.)
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  // SEC-11: withCredentials is REQUIRED so the browser attaches the HttpOnly
  // myledger_rt cookie on cross-origin requests to the API.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Attach JWT and correlation ID to every request
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = store.getState().auth.accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Correlation-Id'] = crypto.randomUUID().replace(/-/g, '');
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  });
  failedQueue = [];
};

// Token refresh interceptor — transparently retries any 401 after refreshing the token.
// SEC-11: The refresh token is NOT read from Redux state — it is sent automatically
// by the browser via the HttpOnly myledger_rt cookie. We only forward the expiring
// access token so the server can re-embed context claims (location/CC/FY).
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RequestConfigWithRetry;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If there's no access token at all, we have no session to refresh.
    const hasSession = store.getState().auth.isAuthenticated;
    if (!hasSession) {
      store.dispatch(logout());
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Send the expiring access token in the body (optional — for context claim
      // extraction). The refresh token travels as the HttpOnly cookie automatically.
      const expiredAccessToken = store.getState().auth.accessToken;
      const response = await axios.post<ApiResponse<LoginResponse>>(
        `${BASE_URL}/auth/refresh`,
        expiredAccessToken ? { accessToken: expiredAccessToken } : {},
        { withCredentials: true }
      );

      const tokens = response.data.data!;
      store.dispatch(tokenRefreshed({
        accessToken: tokens.accessToken,
      }));

      processQueue(null, tokens.accessToken);
      originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      store.dispatch(logout());
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
